import React from 'react';
import { faArrowRightArrowLeft } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/agent-handovers.jpg
import agentHandoversImg from '../../../assets/images/blogs/agent-handovers.jpg';

const AgentHandovers = () => (
    <BlogPost
        title="Agents, Handovers, and Shared Systems"
        subtitle="The interconnected organisation doesn't need everyone speaking the same language - it needs clear contracts at the boundaries."
        category="Architecture"
        categoryIcon={faArrowRightArrowLeft}
        color="#003366"
        date="February 2026"
        readTime="5 min read"
        image={agentHandoversImg}
        imageAlt="Agent handovers - explicit contracts between process boundaries"
        ctaHeading="Ready to connect your processes?"
        ctaText="Book a discovery call and we'll show you how explicit handover contracts create reliable agent coordination."
        seoDescription="How explicit handover contracts between agentic processes work better than forcing shared semantics across organisational boundaries."
        seoKeywords="agent handovers, process integration, semantic alignment, context graphs, agentic systems, Nebulyx"
        slug="agent-handovers"
    >
        <p>
            Monday reflections. 🤔
        </p>

        <p>
            Organisations aren't single processes. They're webs of interconnected but
            disparate processes that need to work together.
        </p>

        <p>
            Marketing qualifies leads → sales closes them. Finance invoices → operations
            delivers. Each process has its own logic, its own language, its own way of
            seeing the world.
        </p>

        <h3>The Alignment Assumption</h3>

        <p>
            Traditional integration tried to solve this with shared ontologies. Get everyone
            agreeing on what "customer" means, what "qualified" means, what "ready" means.
        </p>

        <p>
            Lots of back and forth - and still, Sales and Marketing argue about lead quality.
        </p>

        <blockquote className="blog-post-quote">
            "The problem isn't alignment. It's the assumption that alignment is needed."
        </blockquote>

        <h3>Different Views, Both Valid</h3>

        <p>
            Each process has its own semantics - and that's fine. Marketing understands
            "qualified lead" as engagement score, content interactions, campaign response.
            Sales understands it as budget confirmed, decision maker identified, timeline
            established.
        </p>

        <p>
            Different views of the same entity. Both valid. Both useful. Neither wrong.
        </p>

        <p>
            The problem comes when we try to force a single definition across both - we
            end up with something so abstract it serves neither well.
        </p>

        <h3>Explicit Handovers, Not Shared Semantics</h3>

        <p>
            What actually works is explicit interface contracts between agentic-specific
            processes.
        </p>

        <p>
            Marketing agent says: here's the lead, here's why I qualified it, here's the
            context at this point in time. Sales agent receives that, applies its own lens,
            continues.
        </p>

        <p>
            The handover isn't a semantic alignment exercise - it's a contract. Here's
            what I'm giving you, here's what you need.
        </p>

        <p>
            Both agents interact with the same CRM via the same API. The CRM is the source
            of truth for entity identity. Each agent handles its own process-specific
            interpretation of that entity.
        </p>

        <h3>Validation at the Point of Action</h3>

        <p>
            For semantic correctness, each agent validates against its own domain
            constraints before any writeback.
        </p>

        <p>
            Marketing agent won't update lead status unless its criteria are met. Sales
            agent won't progress opportunity unless its gates are satisfied.
        </p>

        <p>
            Each agent is accountable for its own decisions within its own bounded space.
        </p>

        <p>
            The context graph captures the full trace: which agent, what decision, what
            state, what was written, under what authority. That's your audit trail and
            your integration lineage in one artefact.
        </p>

        <h3>The Shift</h3>

        <p>
            Design explicit handover contracts between processes. Let each process own
            its own semantics. Use shared systems as identity truth. Capture the full
            context at every decision point.
        </p>

        <p>
            The interconnected organisation doesn't need everyone speaking the same language.
            It needs clear contracts at the boundaries and agents accountable within their
            own domains.
        </p>

        <p>
            That's how disparate processes actually work together. 🎯
        </p>
    </BlogPost>
);

export default AgentHandovers;
